import React, { useState, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Navbar from '../components/Navbar.tsx';
import Footer from '../components/Footer.tsx';
import { ResultGroup } from '../components/ResultGroup.tsx';
import { CollapsibleResultGroup } from '../components/CollapsibleResultGroup.tsx';
import FileTreeNavigation from '../components/FileTreeNavigation.tsx';
import EnhancedFileTreeNavigation from '../components/EnhancedFileTreeNavigation.tsx';
import CodePanel from '../components/CodePanel.tsx';
import { CodeDrawer } from '../components/CodeDrawer.tsx';
import { ScanSkeleton } from '../components/ui/CategorySkeleton.tsx';
import { ProgressBar } from '../components/ui/ProgressBar.tsx';
import { SeverityFilter, type SeverityLevel } from '../components/ui/SeverityFilter.tsx';
import { DuplicateToggle } from '../components/ui/DuplicateToggle.tsx';
import SignupModal from '../components/SignupModal.tsx';
import { ScanFinding, GroupedFindings } from '../types/scan.ts';
import { 
  filterGroupedFindingsBySeverity, 
  getTotalFindingsCount 
} from '../utils/resultFilters.ts';
import { deduplicateFindings, expandGroupedFindings, getDeduplicationStats } from '../utils/findingUtils.ts';
import { getScannerDisplayName, getScannerDescription } from '../utils/scannerDisplayNames.ts';
import { Loader2, Shield, Eye, FileCheck, Activity, Clock, HardDrive, Github, Upload, Info, Lock, CheckCircle, Filter, Star, ArrowRight, X, Sparkles, ChevronLeft, ChevronRight } from 'lucide-react';
import { useScanResults } from '../hooks/useScanResults.ts';
import { usePlanContext } from '../hooks/usePlanContext.ts';
import { COPY } from '../constants/copy.ts';
import AIFixAssistant from '../components/AIFixAssistant.tsx';

// Category metadata with icons and descriptions
const CATEGORY_METADATA = {
  security: {
    label: 'Security',
    icon: Shield,
    description: 'Vulnerabilities, secrets, and security misconfigurations',
    color: 'red',
    maxPossible: 6 // secrets, hardcoded_credentials, insecure_defaults, sdk_exposure, unrestricted_api, rbac_misconfig
  },
  privacy: {
    label: 'Privacy',
    icon: Eye,
    description: 'PII exposure, logging issues, and privacy compliance',
    color: 'blue',
    maxPossible: 2 // privacy_log, public_bucket
  },
  compliance: {
    label: 'Compliance',
    icon: FileCheck,
    description: 'RLS, RBAC, headers, and legal requirements',
    color: 'green',
    maxPossible: 3 // rls_validator, netlify_headers, legal_presence
  },
  miscellaneous: {
    label: 'Miscellaneous',
    icon: Activity,
    description: 'Other issues and general recommendations',
    color: 'gray',
    maxPossible: 1 // spa_redirect
  }
} as const;

// Issues Score Card Component (same as ScanResultsScreen)
const IssuesScoreCard: React.FC<{
  title: string;
  totalIssues: number;
  maxPossible: number;
  icon: React.ComponentType<any>;
  tooltip: string;
}> = ({ title, totalIssues, maxPossible, icon: Icon, tooltip }) => {
  // Calculate score percentage: 100 - (totalIssues / maxPossible) * 100, minimum 0
  const scorePercent = Math.max(0, Math.round(100 - (totalIssues / maxPossible) * 100));
  
  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-400';
    if (score >= 60) return 'text-yellow-400';
    return 'text-red-400';
  };

  const getScoreBackground = (score: number) => {
    if (score >= 80) return 'bg-green-900/20 border-green-700/50';
    if (score >= 60) return 'bg-yellow-900/20 border-yellow-700/50';
    return 'bg-red-900/20 border-red-700/50';
  };

  // Show "All Clear!" state for zero issues
  if (totalIssues === 0) {
    return (
      <div className="p-4 rounded-lg border bg-green-900/20 border-green-700/50 group relative">
        <div className="flex items-center gap-2 mb-2">
          <Icon className="w-5 h-5 text-green-400" />
          <span className="text-sm font-medium text-green-300">{title}</span>
          <Info className="w-4 h-4 text-gray-500 opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>
        <div className="flex items-center justify-center">
          <CheckCircle className="w-8 h-8 text-green-400" />
        </div>
        <div className="text-center mt-2">
          <div className="text-lg font-bold text-green-300">All Clear!</div>
        </div>
        
        {/* Tooltip */}
        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10">
          {tooltip}
        </div>
      </div>
    );
  }

  return (
    <div className={`p-4 rounded-lg border ${getScoreBackground(scorePercent)} group relative`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-5 h-5 text-gray-400" />
        <span className="text-sm font-medium text-gray-300">{title}</span>
        <Info className="w-4 h-4 text-gray-500 opacity-0 group-hover:opacity-100 transition-opacity" />
      </div>
      <div className={`text-2xl font-bold ${getScoreColor(scorePercent)} mb-1`}>
        {scorePercent}%
      </div>
      <div className="text-xs text-gray-400">
        {totalIssues} of {maxPossible} potential issue{maxPossible !== 1 ? 's' : ''} found
      </div>
      
      {/* Tooltip */}
      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10">
        {tooltip}
      </div>
    </div>
  );
};

// Congratulatory Banner Component
const CongratulationsBanner: React.FC<{
  bestPractices: string[];
}> = ({ bestPractices }) => {
  return (
    <div className="mb-8 p-6 rounded-lg border border-green-700/50 bg-green-900/20">
      <div className="text-center mb-4">
        <div className="text-2xl mb-2">🎉</div>
        <h2 className="text-xl font-semibold text-green-300 mb-2">
          No issues detected! Great job keeping your code secure and compliant.
        </h2>
      </div>
      
      {bestPractices.length > 0 && (
        <div className="max-w-2xl mx-auto">
          <ul className="space-y-2">
            {bestPractices.slice(0, 3).map((practice, index) => (
              <li key={index} className="flex items-center gap-2 text-green-400">
                <CheckCircle className="w-4 h-4 flex-shrink-0" />
                <span className="text-sm">{practice}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

// Category Section Component - Updated to match ScanResultsScreen
const CategorySection: React.FC<{
  categoryKey: keyof GroupedFindings;
  findings: ScanFinding[];
  onJumpToFinding?: (finding: ScanFinding) => void;
}> = ({ categoryKey, findings, onJumpToFinding }) => {
  const category = CATEGORY_METADATA[categoryKey];
  const Icon = category.icon;
  
  const issueCount = findings.length;

  return (
    <div className="mb-8">
      {/* Category Header */}
      <div className="flex items-center gap-3 mb-4">
        <div className={`p-2 rounded-lg bg-${category.color}-900/20 border border-${category.color}-700/50`}>
          <Icon className={`w-6 h-6 text-${category.color}-400`} />
        </div>
        <div className="flex-1">
          <h2 className="text-xl font-semibold text-white">{category.label}</h2>
          <p className="text-sm text-gray-400">{category.description}</p>
        </div>
        <div className="text-right">
          {issueCount === 0 ? (
            <span className="text-sm text-green-400">(no issues)</span>
          ) : (
            <span className="text-sm text-gray-300">
              ({issueCount} issue{issueCount !== 1 ? 's' : ''})
            </span>
          )}
        </div>
      </div>

      {/* Category Content */}
      {issueCount === 0 ? (
        <div className="p-6 rounded-lg border border-green-700/50 bg-green-900/20 text-center">
          <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-3">
            <Icon className="w-6 h-6 text-white" />
          </div>
          <h3 className="text-lg font-medium text-green-300 mb-2">All Clear!</h3>
          <p className="text-green-400">
            ✅ No {category.label.toLowerCase()} issues found in this demo
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Group findings by scanner and use CollapsibleResultGroup */}
          {Object.entries(
            findings.reduce((acc, finding) => {
              if (!acc[finding.module_id]) {
                acc[finding.module_id] = [];
              }
              acc[finding.module_id].push(finding);
              return acc;
            }, {} as Record<string, ScanFinding[]>)
          ).map(([scannerId, scannerFindings]) => {
            return (
              <CollapsibleResultGroup
                key={scannerId}
                findings={scannerFindings}
                title={getScannerDisplayName(scannerId)}
                description={getScannerDescription(scannerId)}
                isProUser={true} // Demo mode shows all features
                defaultExpanded={false}
                onJumpToFinding={onJumpToFinding}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

// Demo Signup Banner Component - appears at top of results for unauthenticated users
const DemoSignupBanner: React.FC<{ onSignupClick: () => void; onClose?: () => void }> = ({ onSignupClick, onClose }) => {
  return (
    <div 
      data-cy="demo-signup-banner"
      className="mb-6 p-4 bg-gradient-to-r from-blue-900/30 to-purple-900/30 border border-blue-700/50 rounded-lg"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-600 rounded-full">
            <Star className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="text-white font-semibold">{COPY.DEMO_RESULTS_BANNER}</h3>
            <p className="text-blue-200 text-sm">Get 10 free scans with your account</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-cy="signup-modal-trigger"
            onClick={onSignupClick}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center gap-2 font-medium"
          >
            Sign Up Free
            <ArrowRight className="w-4 h-4" />
          </button>
          {onClose && (
            <button
              onClick={onClose}
              className="p-1 hover:bg-gray-700 rounded-full transition-colors text-gray-400 hover:text-white"
              aria-label="Close banner"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Demo CTA Component
const DemoCTA: React.FC<{ onSignupClick: () => void }> = ({ onSignupClick }) => {
  const navigate = useNavigate();
  
  return (
    <div className="mt-12 p-8 rounded-lg border border-blue-700/50 bg-blue-900/20">
      <div className="text-center">
        <div className="text-4xl mb-4">⭐</div>
        <h3 className="text-2xl font-bold text-white mb-3">{COPY.DEMO_RESULTS_CTA_HEADER}</h3>
        <p className="text-gray-300 mb-6 max-w-2xl mx-auto">
          {COPY.DEMO_RESULTS_CTA_DESCRIPTION}
        </p>
        <div className="flex justify-center">
          <button
            onClick={onSignupClick}
            className="px-6 py-3 bg-green-500 hover:bg-green-600 text-white font-semibold rounded-lg transition-colors flex items-center gap-2 justify-center"
          >
            <Upload className="w-5 h-5" />
            Sign Up & Upload Your Code
          </button>
        </div>
      </div>
    </div>
  );
};

export default function DemoResultsScreen() {
  console.log('[DemoResultsScreen] Component rendered');
  
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // AI Fix Assistant state management  
  const [isAIAssistantOpen, setIsAIAssistantOpen] = useState(false);
  const [aiAssistantPhase, setAIAssistantPhase] = useState<'initial' | 'loading' | 'generated' | 'error'>('initial');
  
  // Use the shared hook in demo mode
  const { scanData, groupedFindings, categoryScores, loading, error, progress } = useScanResults('demo');
  
  // Authentication and plan context
  const { isAuthenticated } = usePlanContext();
  
  // Signup modal state
  const [showSignupModal, setShowSignupModal] = useState(false);
  
  // Demo banner visibility state
  const [showDemoBanner, setShowDemoBanner] = useState(true);

  // File selection and filtering state - Enhanced to match ScanResultsScreen
  const [selectedFile, setSelectedFile] = useState<string | null>(null);
  const [activeFinding, setActiveFinding] = useState<ScanFinding | null>(null);
  const [hideDuplicates, setHideDuplicates] = useState(true); // Default to hiding duplicates
  const [isCodePanelExpanded, setIsCodePanelExpanded] = useState(false);

  // Get severity filters from URL
  const selectedSeverities: SeverityLevel[] = useMemo(() => {
    const filters = searchParams.get('severity');
    if (filters) {
      return filters.split(',').filter(f => 
        ['critical', 'high', 'medium', 'low', 'info'].includes(f)
      ) as SeverityLevel[];
    }
    return ['critical', 'high', 'medium', 'low', 'info'];
  }, [searchParams]);

  // Filter findings based on severity selection
  const filteredFindings = useMemo(() => {
    if (!groupedFindings) return { security: [], privacy: [], compliance: [], miscellaneous: [] };
    return filterGroupedFindingsBySeverity(groupedFindings, selectedSeverities);
  }, [groupedFindings, selectedSeverities]);

  // Apply deduplication to each category
  const deduplicatedFindings = useMemo(() => {
    const result = { security: [], privacy: [], compliance: [], miscellaneous: [] } as any;
    
    Object.entries(filteredFindings).forEach(([category, findings]) => {
      const groupedCategory = deduplicateFindings(findings);
      result[category] = expandGroupedFindings(groupedCategory, hideDuplicates);
    });
    
    return result;
  }, [filteredFindings, hideDuplicates]);

  // Get deduplication statistics for all findings
  const deduplicationStats = useMemo(() => {
    if (!groupedFindings) return { totalFindings: 0, uniqueFindings: 0, duplicateGroups: 0, duplicatesRemoved: 0, duplicatePercentage: 0 };
    const allFindings = Object.values(groupedFindings).flat();
    return getDeduplicationStats(allFindings);
  }, [groupedFindings]);

  const totalFindings = groupedFindings ? getTotalFindingsCount(groupedFindings) : 0;
  const filteredTotalFindings = getTotalFindingsCount(filteredFindings);
  const deduplicatedTotalFindings = getTotalFindingsCount(deduplicatedFindings);

  // Get all findings for file tree
  const allFindings = useMemo(() => {
    if (!groupedFindings) return [];
    return Object.values(groupedFindings).flat();
  }, [groupedFindings]);

  // Get actual files from the scan - use demo files
  const getAllFiles = useMemo(() => {
    const extractedFiles = scanData?.metadata?.extracted_files;
    
    // If we have extracted files metadata, use it
    if (extractedFiles && extractedFiles.length > 0) {
      return extractedFiles;
    }
    
    // Fallback: get files that have findings
    const findingFiles = new Set(allFindings.map(f => f.file_path));
    return Array.from(findingFiles).sort();
  }, [scanData?.metadata?.extracted_files, allFindings]);

  // Get findings for selected file
  const selectedFileFindings = useMemo(() => {
    if (!selectedFile || !allFindings.length) return [];
    return allFindings.filter(finding => finding.file_path === selectedFile);
  }, [selectedFile, allFindings]);

  // Handle file selection from FileTreeNavigation
  const handleFileSelect = useCallback((filePath: string | null) => {
    console.log('[DemoResultsScreen] File selected:', filePath);
    setSelectedFile(filePath);
    setActiveFinding(null); // Clear active finding when selecting a different file
  }, []);

  // Handle jumping to a specific finding in the code
  const handleJumpToFinding = useCallback((finding: ScanFinding) => {
    console.log('[DemoResultsScreen] Jumping to finding:', finding.file_path, 'line', finding.line);
    setSelectedFile(finding.file_path);
    setActiveFinding(finding);
  }, []);

  // Handle CodePanel close
  const handleCodePanelClose = () => {
    console.log('[DemoResultsScreen] Closing code panel');
    setSelectedFile(null);
    setActiveFinding(null);
  };

  // Handle code panel expand/collapse
  const handleCodePanelExpand = () => {
    console.log('[DemoResultsScreen] Toggling code panel expand:', !isCodePanelExpanded);
    setIsCodePanelExpanded(!isCodePanelExpanded);
  };

  const handleFiltersChange = (filters: SeverityLevel[]) => {
    console.log('[DemoResultsScreen] Severity filters changed:', filters);
  };

  // Handle signup modal open
  const handleSignupClick = () => {
    console.log('[DemoResultsScreen] Opening signup modal from demo results');
    setShowSignupModal(true);
  };

  // Handle AI Assistant toggle
  const handleAIAssistantToggle = useCallback(() => {
    setIsAIAssistantOpen(prev => !prev);
    if (!isAIAssistantOpen) {
      setAIAssistantPhase('initial');
    }
  }, [isAIAssistantOpen]);

  const handleAIAssistantClose = useCallback(() => {
    setIsAIAssistantOpen(false);
    setAIAssistantPhase('initial');
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 text-white">
        <Navbar />
        <div className="container mx-auto px-4 py-8">
          {progress ? (
            <div className="max-w-2xl mx-auto">
              <div className="text-center mb-8">
                <h2 className="text-2xl font-semibold mb-2">Running Demo Scan</h2>
                <p className="text-gray-400">
                  Simulating security scan with sample findings
                </p>
              </div>
              <ProgressBar progress={progress} />
            </div>
          ) : (
            <ScanSkeleton />
          )}
        </div>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-900 text-white">
        <Navbar />
        <div className="container mx-auto px-4 py-8">
          <div className="text-center py-12">
            <h1 className="text-2xl font-bold text-red-400 mb-4">Error Loading Demo</h1>
            <p className="text-gray-400 mb-6">{error}</p>
            <button 
              onClick={() => window.location.reload()}
              className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!scanData || !groupedFindings) {
    return (
      <div className="min-h-screen bg-gray-900 text-white">
        <Navbar />
        <div className="container mx-auto px-4 py-8">
          <div className="text-center py-12">
            <h1 className="text-2xl font-bold text-gray-400 mb-4">No Demo Data</h1>
            <p className="text-gray-400">Unable to generate demo results.</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <Navbar />
      
      {/* Main Layout Container with ARIA landmarks - Responsive Layout */}
      <main className="flex h-[calc(100vh-64px)] overflow-hidden" role="main">
        {/* File Explorer Navigation - ARIA landmark */}
        {scanData && (
          <nav 
            aria-label="File Explorer" 
            className={`flex-shrink-0 border-r border-gray-700 transition-all duration-300 ${
              isAIAssistantOpen ? 'w-64' : 'w-80'
            }`}
          >
            <EnhancedFileTreeNavigation
              findings={allFindings}
              allFiles={getAllFiles}
              selectedFile={selectedFile}
              onFileSelect={handleFileSelect}
              isProUser={true} // Demo mode shows all features
              className="h-full"
            />
          </nav>
        )}
        
        {/* Code Viewer - Middle Column - ARIA landmark */}
        {selectedFile && (
          <aside 
            aria-label="Code Viewer" 
            className={`flex-shrink-0 border-r border-gray-700 transition-all duration-300 ${
              isAIAssistantOpen 
                ? isCodePanelExpanded ? 'w-80' : 'w-64'  // Smaller when AI assistant open
                : isCodePanelExpanded ? 'w-[32rem]' : 'w-80'  // Normal when AI assistant closed
            }`}
          >
            <CodePanel
              fileName={selectedFile}
              findings={selectedFileFindings}
              isProUser={true} // Demo mode shows all features
              onClose={handleCodePanelClose}
              onExpand={handleCodePanelExpand}
              isExpanded={isCodePanelExpanded}
              className="h-full"
              activeFinding={activeFinding || undefined}
              scanId="demo" // Demo mode
            />
          </aside>
        )}
        
        {/* Scan Results - Main Content Area - ARIA landmark */}
        <section 
          aria-label="Scan Results" 
          className="flex-1 min-w-0 bg-gray-900 overflow-hidden relative"
        >
          <div className="h-full overflow-y-auto">
            <div className="px-6 py-8 pb-32">
              <div className={`mx-auto transition-all duration-300 ${
                // Dynamic max width based on available space and AI assistant state
                isAIAssistantOpen
                  ? selectedFile && isCodePanelExpanded 
                    ? 'max-w-3xl' // Smallest when both code panel expanded and AI assistant open
                    : selectedFile 
                      ? 'max-w-4xl' // Small when code panel normal and AI assistant open
                      : 'max-w-5xl' // Medium when no code panel but AI assistant open
                  : selectedFile && isCodePanelExpanded 
                    ? 'max-w-4xl' // Normal when code panel expanded, no AI assistant
                    : selectedFile 
                      ? 'max-w-5xl' // Normal when code panel normal, no AI assistant
                      : 'max-w-6xl' // Largest when no code panel and no AI assistant
              }`}>
                <div>
                  {/* Header with demo metadata */}
                  {scanData && (
                    <header className="mb-6 p-4 bg-gray-800 rounded-lg border border-gray-700">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="flex items-center gap-2">
                            <Github className="w-5 h-5 text-blue-400" />
                            <span className="text-sm font-medium text-gray-300">
                              Demo Scan Results
                            </span>
                            <span className="ml-2 text-sm bg-blue-600 text-white px-2 py-1 rounded">
                              Demo Mode
                            </span>
                          </div>
                          {scanData?.metadata?.file_size && (
                            <div className="flex items-center gap-2 text-sm text-gray-400">
                              <HardDrive className="w-4 h-4" />
                              {scanData.metadata.file_size > 1024 * 1024 
                                ? `${(scanData.metadata.file_size / (1024 * 1024)).toFixed(1)} MB`
                                : `${(scanData.metadata.file_size / 1024).toFixed(1)} KB`}
                            </div>
                          )}
                        </div>
                        
                        {/* AI Fix Assistant Toggle Button */}
                        {allFindings.length > 0 && (
                          <button
                            onClick={handleAIAssistantToggle}
                            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                              isAIAssistantOpen
                                ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/25'
                                : 'bg-gray-700 hover:bg-gray-600 text-gray-300 hover:text-white'
                            }`}
                            title={isAIAssistantOpen ? 'Close AI Fix Assistant' : 'Open AI Fix Assistant (Demo)'}
                          >
                            <Sparkles className={`w-4 h-4 ${isAIAssistantOpen ? 'animate-pulse' : ''}`} />
                            <span>AI Fixes</span>
                            <span className="text-xs bg-green-600 text-white px-1 py-0.5 rounded">DEMO</span>
                            {isAIAssistantOpen ? (
                              <ChevronRight className="w-4 h-4" />
                            ) : (
                              <ChevronLeft className="w-4 h-4" />
                            )}
                          </button>
                        )}
                      </div>
                    </header>
                  )}

                  {/* Results Header */}
                  <header className="mb-6">
                    <div className="flex items-center gap-4 mb-4">
                      <h1 className="text-3xl font-bold">Demo Scan Results</h1>
                      <div className="text-sm text-gray-400">
                        {hideDuplicates && deduplicationStats.duplicatesRemoved > 0
                          ? `${deduplicatedTotalFindings} unique issue${deduplicatedTotalFindings !== 1 ? 's' : ''} (${deduplicationStats.duplicatesRemoved} duplicates hidden)`
                          : deduplicatedTotalFindings === totalFindings 
                            ? `${totalFindings} issue${totalFindings !== 1 ? 's' : ''} found`
                            : `${deduplicatedTotalFindings} of ${totalFindings} issue${totalFindings !== 1 ? 's' : ''} shown`
                        }
                      </div>
                    </div>
                    
                    {selectedFile && (
                      <div className="text-gray-400 mb-2">
                        Showing results for: <span className="text-blue-400 font-mono">{selectedFile}</span>
                      </div>
                    )}
                    
                    <p className="text-gray-400 mb-4">
                      Sample security findings to demonstrate SafeVibe capabilities
                    </p>

                    {/* Filters */}
                    <div className="flex flex-wrap items-center gap-4">
                      <SeverityFilter onFiltersChange={handleFiltersChange} isSticky={true} />
                      <DuplicateToggle
                        hideDuplicates={hideDuplicates}
                        onToggle={setHideDuplicates}
                        duplicateCount={deduplicationStats.duplicatesRemoved}
                        totalCount={totalFindings}
                      />
                    </div>
                  </header>

                  {/* Calculate issues by category using actual grouped findings */}
                  {(() => {
                    const securityIssues = groupedFindings.security?.length || 0;
                    const privacyIssues = groupedFindings.privacy?.length || 0;
                    const complianceIssues = groupedFindings.compliance?.length || 0;
                    const miscellaneousIssues = groupedFindings.miscellaneous?.length || 0;
                    const totalIssues = securityIssues + privacyIssues + complianceIssues + miscellaneousIssues;
                    
                    // Generate best practices from scanner user_value fields for congratulations banner
                    const bestPractices: string[] = [];
                    if (securityIssues === 0) {
                      bestPractices.push("Environment variables used for secrets");
                      bestPractices.push("Standard security headers present");
                    }
                    if (privacyIssues === 0) {
                      bestPractices.push("No PII detected in logs");
                    }
                    if (complianceIssues === 0) {
                      bestPractices.push("Row Level Security properly configured");
                      bestPractices.push("Legal compliance documents present");
                    }

                    return (
                      <>
                        {/* Congratulations Banner for zero issues */}
                        {totalIssues === 0 && (
                          <CongratulationsBanner bestPractices={bestPractices} />
                        )}

                        {/* Issues Score Cards */}
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
                          <IssuesScoreCard
                            title="Security"
                            totalIssues={securityIssues}
                            maxPossible={CATEGORY_METADATA.security.maxPossible}
                            icon={Shield}
                            tooltip="Security vulnerabilities and misconfigurations"
                          />
                          <IssuesScoreCard
                            title="Privacy"
                            totalIssues={privacyIssues}
                            maxPossible={CATEGORY_METADATA.privacy.maxPossible}
                            icon={Eye}
                            tooltip="Privacy compliance and data protection"
                          />
                          <IssuesScoreCard
                            title="Compliance"
                            totalIssues={complianceIssues}
                            maxPossible={CATEGORY_METADATA.compliance.maxPossible}
                            icon={FileCheck}
                            tooltip="Regulatory and policy compliance"
                          />
                          <IssuesScoreCard
                            title="Overall"
                            totalIssues={totalIssues}
                            maxPossible={CATEGORY_METADATA.security.maxPossible + CATEGORY_METADATA.privacy.maxPossible + CATEGORY_METADATA.compliance.maxPossible + CATEGORY_METADATA.miscellaneous.maxPossible}
                            icon={Activity}
                            tooltip="Combined score across all categories"
                          />
                        </div>
                      </>
                    );
                  })()}

                  {/* Results Categories */}
                  <div className="space-y-8">
                    {Object.entries(deduplicatedFindings).map(([categoryKey, findings]) => {
                      if ((findings as ScanFinding[]).length === 0) return null;
                      
                      return (
                        <CategorySection
                          key={categoryKey}
                          categoryKey={categoryKey as keyof GroupedFindings}
                          findings={findings as ScanFinding[]}
                          onJumpToFinding={handleJumpToFinding}
                        />
                      );
                    })}
                  </div>

                  {/* Demo Signup Banner */}
                  {!isAuthenticated && showDemoBanner && (
                    <DemoSignupBanner 
                      onSignupClick={handleSignupClick} 
                      onClose={() => setShowDemoBanner(false)}
                    />
                  )}

                  {/* Demo CTA */}
                  <DemoCTA onSignupClick={handleSignupClick} />
                </div>
              </div>
            </div>
          </div>
          
          {/* Floating AI Fix Assistant Toggle - Only show when closed and findings exist */}
          {!isAIAssistantOpen && allFindings.length > 0 && (
            <div className="absolute bottom-6 right-6 z-20">
              <button
                onClick={handleAIAssistantToggle}
                className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white p-4 rounded-full shadow-lg shadow-blue-600/25 transition-all duration-200 hover:scale-105 group relative"
                title="Open AI Fix Assistant (Demo)"
              >
                <Sparkles className="w-6 h-6 group-hover:animate-pulse" />
                <span className="absolute -top-2 -right-2 text-xs bg-green-600 text-white px-1 py-0.5 rounded">DEMO</span>
              </button>
            </div>
          )}
        </section>
        
        {/* AI Fix Assistant - Right Column - ARIA landmark */}
        {isAIAssistantOpen && (
          <aside 
            aria-label="AI Fix Assistant" 
            className="flex-shrink-0 transition-all duration-300 ease-in-out"
            style={{ width: '400px' }}
          >
            <AIFixAssistant
              isOpen={isAIAssistantOpen}
              onClose={handleAIAssistantClose}
              scanId="demo" // Demo mode
              findings={allFindings}
              totalFindings={allFindings.length}
            />
          </aside>
        )}
      </main>
      
      {/* Mobile Code Drawer - Only show on mobile when file is selected */}
      {selectedFile && (
        <div className="lg:hidden">
          <CodeDrawer
            fileName={selectedFile}
            findings={selectedFileFindings}
            isProUser={true} // Demo mode shows all features
            onClose={handleCodePanelClose}
            isOpen={true}
            className="z-50"
            activeFinding={activeFinding || undefined}
          />
        </div>
      )}
      
      <Footer />
      
      {/* Signup Modal */}
      <SignupModal
        isOpen={showSignupModal}
        onClose={() => setShowSignupModal(false)}
        source="demo_results"
      />
    </div>
  );
} 